import {
  DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS,
  EMPLOYEE_TIME_SHEET_REPORT_KEY,
  normalizeEmployeeTimeSheetColumns,
  type EmployeeTimeSheetColumnKey,
} from '../constants/employeeTimeSheetColumns';

const COLUMN_PREFS_PREFIX = 'teks-time-report-columns';

function storageKey(reportKey: string, userId?: string): string {
  return userId ? `${COLUMN_PREFS_PREFIX}-${reportKey}-${userId}` : `${COLUMN_PREFS_PREFIX}-${reportKey}`;
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors
  }
}

export function getReportVisibleColumns(
  reportKey: string,
  userId?: string,
  fallback: string[] = [],
): string[] {
  if (reportKey === EMPLOYEE_TIME_SHEET_REPORT_KEY) {
    const key = storageKey(reportKey, userId);
    const stored = readJson<string[] | null>(key, null);
    if (stored === null) {
      return [...DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS];
    }
    const normalized = normalizeEmployeeTimeSheetColumns(stored);
    if (JSON.stringify(stored) !== JSON.stringify(normalized)) {
      writeJson(key, normalized);
    }
    return normalized;
  }

  const stored = readJson<string[] | null>(storageKey(reportKey, userId), null);
  return stored && stored.length > 0 ? stored : fallback;
}

export function saveReportVisibleColumns(
  reportKey: string,
  userId: string | undefined,
  columns: string[],
): string[] {
  const next = reportKey === EMPLOYEE_TIME_SHEET_REPORT_KEY
    ? normalizeEmployeeTimeSheetColumns(columns)
    : columns.filter(Boolean);

  writeJson(storageKey(reportKey, userId), next);
  return next;
}

export function resetReportVisibleColumns(
  reportKey: string,
  userId?: string,
): string[] {
  if (reportKey === EMPLOYEE_TIME_SHEET_REPORT_KEY) {
    const next = [...DEFAULT_EMPLOYEE_TIME_SHEET_COLUMNS];
    writeJson(storageKey(reportKey, userId), next);
    return next;
  }

  writeJson(storageKey(reportKey, userId), []);
  return [];
}

export type { EmployeeTimeSheetColumnKey };
